import {
    deployEnvironment,
    ENVIRONMENT_LABEL,
    useClients,
    useMerchantProviders,
} from '@features/clients';
import {
    Button,
    EmptyState,
    ErrorState,
    FlagKeyPill,
    LoadingRows,
    Modal,
    WriteError,
} from '@shared/ui';
import { useMemo, useState } from 'react';

import { useStartClientsOperation } from '../hooks/use-release-operation';

import { providerFilterView } from './client-selection';
import { clientsEnvironmentView } from './clients-environment';
import { ClientSelector } from './ClientSelector';
import { confirmView } from './operation-confirm';
import { explainReleaseWriteFailure } from './release-error-copy';

import type { ReleaseComponent } from '../schemas/release-component';

/**
 * Turn a release on for chosen clients (ops-csm). Mounted only while open. The
 * environment is the deploy environment, shown and locked. Turn-off for clients
 * is `TurnOffClientsDialog`, behind the typed phrase.
 */
export function TurnOnClientsDialog({
    release,
    onClose,
}: {
    release: ReleaseComponent;
    onClose: () => void;
}) {
    const environment = clientsEnvironmentView(deployEnvironment());
    const clients = useClients(environment.target);
    // A read of its own: when it fails the picker loses the provider filter, not the clients.
    const providers = providerFilterView(useMerchantProviders(environment.target));
    const start = useStartClientsOperation(release.id);
    const [selected, setSelected] = useState<readonly string[]>([]);
    const failure = start.error ? explainReleaseWriteFailure(start.error) : null;
    const options = useMemo(() => clients.data ?? [], [clients.data]);
    const view = confirmView({
        releaseName: release.name,
        enabled: true,
        scope: 'clients',
        clientCount: selected.length,
        flags: (release.members ?? []).map((member) => ({
            key: member.key,
            name: member.name,
            archived: member.archived,
        })),
        environment: ENVIRONMENT_LABEL[deployEnvironment()],
    });

    const submit = () => {
        start.mutate({ enabled: true, tenantIds: selected }, { onSuccess: onClose });
    };

    return (
        <Modal
            open
            onClose={onClose}
            dismissable={!start.isPending}
            title={
                selected.length === 0 ? `Turn on ${release.name} for specific clients` : view.title
            }
            description={view.overrideNotice}
            footer={
                <>
                    <Button variant="ghost" onClick={onClose} disabled={start.isPending}>
                        Cancel
                    </Button>
                    <Button onClick={submit} disabled={start.isPending || selected.length === 0}>
                        {start.isPending ? 'Starting…' : view.confirmLabel}
                    </Button>
                </>
            }
        >
            <div className="flex flex-col gap-3 text-[13px]">
                <p className="font-medium text-content-strong">{environment.label}</p>
                {clients.isPending ? (
                    <LoadingRows label="Loading clients…" />
                ) : clients.isError ? (
                    <ErrorState error={clients.error} onRetry={() => void clients.refetch()} />
                ) : options.length === 0 ? (
                    <EmptyState title="No clients" description="This environment has no clients." />
                ) : (
                    <ClientSelector
                        clients={options}
                        selected={selected}
                        onChange={setSelected}
                        providers={providers}
                        disabled={start.isPending}
                    />
                )}
                <div>
                    <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-content-muted">
                        {view.summary}
                    </p>
                    <ul className="flex flex-col gap-1.5">
                        {view.flags.map((flag) => (
                            <li key={flag.key} className="flex items-center gap-2">
                                <FlagKeyPill>{flag.key}</FlagKeyPill>
                                {flag.note !== undefined && (
                                    <span className="text-xs text-content-muted">{flag.note}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
                {failure ? (
                    <div
                        role="alert"
                        className="rounded-lg border border-danger-line bg-danger-surface px-3.5 py-2.5 text-[12.5px] leading-relaxed text-danger-text"
                    >
                        <p className="font-semibold">{failure.title}</p>
                        <p className="mt-0.5 text-danger-muted">{failure.body}</p>
                        {failure.detail !== undefined && (
                            <p className="mt-1.5 text-danger-muted">{failure.detail}</p>
                        )}
                    </div>
                ) : (
                    start.error && <WriteError error={start.error} />
                )}
            </div>
        </Modal>
    );
}
